import { useMutation, useQuery } from '@apollo/client'
import { Grid, Typography } from '@mui/material'
import {
    DataGridPro,
    GridCellEditStopParams,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import { isDeepEqual } from '@mui/x-data-grid/internals'
import {
    GetCandidateProfileForRecruiterQuery,
    GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import { formatDate } from 'date-fns'
import { FC, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
    Attributes as AttributesQuery,
    UpdateAnalystClassYear,
    UpdateCandidateMbaTier,
    UpdateCitizenship,
    UpdateCpiPlaced,
    UpdateDealSourcingRole,
    UpdateEthnicity,
    UpdateGender,
    UpdateHometown,
    UpdateLanguages,
    UpdateLgbtq,
    UpdatePersonalityAttributes,
    UpdateRealEstateDealExperience,
    UpdateReferenceAttribute,
    UpdateRevenueExperience,
    UpdateTier,
    UpdateVeteranStatus,
    UpdateVisaStatus,
} from 'recruiter/graphql/attributes.gql'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem } from 'shared/utils'
import { Card } from '../styled'
import { ChipCell } from './ChipCell'
import { EditCell } from './EditCell'

interface AttributeRowBase {
    category: string
    id: string
    options?: DropdownOption[] | null
    update: string
    handleSave: (params: GridCellEditStopParams) => void
    handleReset: (params: GridCellEditStopParams) => void
}

export type AttributeRow =
    // Attributes that allow only a single selection from a dropdown
    | (AttributeRowBase & {
          attribute?: DropdownOption | null
          multiple?: false
          freetext?: false
          yearpicker?: false
      })
    // Attributes that allow multiple selections from a dropdown
    | (AttributeRowBase & {
          attribute: DropdownOption[]
          multiple: true
          freetext?: false
          yearpicker?: false
      })
    // Attributes that allow for free text entry
    | (AttributeRowBase & {
          attribute?: string | null
          multiple?: false
          freetext: true
          yearpicker?: false
      })
    // Attributes that allow for year picker entry
    | (AttributeRowBase & {
          attribute?: Date | null
          multiple?: false
          freetext?: false
          yearpicker: true
      })

interface Props {
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
}

const Attributes: FC<Props> = ({ refetch }) => {
    const toast = useToast()
    const gridRef = useGridApiRef()
    const { candidateId } = useParams<{ candidateId: string }>()
    const { data, refetch: refetchAttributes } = useQuery(AttributesQuery, {
        variables: { candidateId },
    })
    const [updateCitizenship] = useMutation(UpdateCitizenship)
    const [updateGender] = useMutation(UpdateGender)
    const [updateEthnicity] = useMutation(UpdateEthnicity)
    const [updateVeteranStatus] = useMutation(UpdateVeteranStatus)
    const [updateTier] = useMutation(UpdateTier)
    const [updateLgbtq] = useMutation(UpdateLgbtq)
    const [updateLanguages] = useMutation(UpdateLanguages)
    const [updateVisaStatus] = useMutation(UpdateVisaStatus)
    const [updateHometown] = useMutation(UpdateHometown)
    const [updatePersonalityAttributes] = useMutation(
        UpdatePersonalityAttributes
    )
    const [updateRealEstateDealExperience] = useMutation(
        UpdateRealEstateDealExperience
    )
    const [updateRevenueExperience] = useMutation(UpdateRevenueExperience)
    const [updateCpiPlaced] = useMutation(UpdateCpiPlaced)
    const [updateDealSourcingRole] = useMutation(UpdateDealSourcingRole)
    const [updateReferenceAttribute] = useMutation(UpdateReferenceAttribute)
    const [updateCandidateMbaTier] = useMutation(UpdateCandidateMbaTier)
    const [updateAnalystClassYear] = useMutation(UpdateAnalystClassYear, {
        onCompleted: (data) => {
            if (data.updateAnalystClassYear.success) {
                refetchAttributes()
                toast({
                    severity: 'success',
                    title: 'Success',
                    message: 'Analyst class year has been updated',
                })
            }
        },
        onError: (error) => {
            toast({
                severity: 'error',
                title: 'Error',
                message: error.message || 'Failed to update analyst class year',
            })
        },
    })

    const handleResetWithErrorToast = useCallback(
        (params: GridCellEditStopParams, e?: string | null) => {
            params.row.handleReset(params)
            toast({
                title: 'Error',
                message: e || `Failed to update ${params.row.category}.`,
            })
        },
        [toast]
    )

    const updateFormatting = (
        updatedAt?: string | null,
        updatedBy?: {
            __typename?: string
            firstName: string
            nickname?: string | null
            lastName: string
        } | null,
        createdAt?: string,
        createdBy?: {
            __typename?: string
            firstName: string
            nickname?: string | null
            lastName: string
        } | null
    ) => {
        if (!updatedAt && !createdAt && !updatedBy && !createdBy) return '-'
        const date = updatedAt || createdAt
        const user = updatedBy || createdBy
        const userName = `${user?.nickname || user?.firstName || ''}${user?.lastName ? ` ${user.lastName}` : ''}`
        const formattedDate = date ? formatDate(date, 'MM/dd/yyyy h:mm a') : ''
        const formattedUser = userName || 'Unknown User'
        return `${formattedDate} ${formattedUser}`
    }

    const extractAuditDataFromArray = (
        arrayWithAuditData: Array<{
            updatedAt?: any
            createdAt?: any
            [key: string]: any
        }> | null = []
    ) => {
        const toSort = arrayWithAuditData ? [...arrayWithAuditData] : []
        const sorted = toSort?.sort((a, b) => {
            const dateA = new Date(a.updatedAt || a.createdAt || 0).getTime()
            const dateB = new Date(b.updatedAt || b.createdAt || 0).getTime()
            return dateB - dateA
        })
        return [
            sorted?.[0]?.updatedAt,
            sorted?.[0]?.updatedBy || sorted?.[0]?.updatedByUser,
            sorted?.[0]?.createdAt,
            sorted?.[0]?.createdBy || sorted?.[0]?.createdByUser,
        ]
    }

    const stateOrProvinceOptions = useMemo(() => {
        if (data?.candidate?.hometown?.country?.value === 'Canada')
            return data?.provinces
        if (data?.candidate?.hometown?.country?.value === 'United States')
            return data?.states
        return []
    }, [data])

    const stateOrProvinceAttribute = useMemo(() => {
        if (!data?.candidate?.hometown?.country) return null

        let attribute: DropdownOption | null | undefined

        if (data.candidate.hometown.country.value === 'United States') {
            attribute = data.candidate.hometown.state
        }

        if (data.candidate.hometown.country.value === 'Canada') {
            attribute = data.candidate.hometown.province
        }

        return attribute ?? null
    }, [data])

    const rows: AttributeRow[] = useMemo(() => {
        if (!data?.candidate) return []

        return [
            {
                category: 'Analyst Class Year',
                id: 'analystClassYear',
                attribute: data.candidate.analystClassYear
                    ? new Date(data.candidate.analystClassYear, 0, 1)
                    : null,
                yearpicker: true,
                update: '-', // Audit info not available - field stored directly on candidate table
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const newYear =
                        row.attribute instanceof Date
                            ? row.attribute.getFullYear()
                            : null
                    const currentYear = data.candidate?.analystClassYear
                    const hasNotChanged = newYear === currentYear
                    if (hasNotChanged) return

                    updateAnalystClassYear({
                        variables: {
                            candidateId: candidateId || '',
                            analystClassYear: newYear,
                        },
                    })
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.analystClassYear
                            ? new Date(data.candidate.analystClassYear, 0, 1)
                            : null,
                    })
                },
            },
            {
                category: 'Gender',
                id: 'gender',
                attribute: data.candidate.gender?.gender,
                options: data.genders,
                update: updateFormatting(
                    data.candidate.gender?.updatedAt,
                    data.candidate.gender?.updatedByUser,
                    data.candidate.gender?.createdAt,
                    data.candidate.gender?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id === data.candidate?.gender?.gender?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateGender({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    genderId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response?.data?.updateGender.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Gender has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateGender?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.gender?.gender,
                    })
                },
            },
            {
                category: 'Race/Ethnicity',
                id: 'race',
                attribute:
                    data.candidate.ethnicities?.map((ethnicity) => ({
                        id: ethnicity.ethnicity?.id ?? '',
                        value: ethnicity.ethnicity?.value ?? '',
                        updatedAt: ethnicity.updatedAt,
                        updatedBy: ethnicity.updatedByUser,
                        createdAt: ethnicity.createdAt,
                        createdBy: ethnicity.createdByUser,
                    })) ?? [],
                multiple: true,
                options: data.ethnicities,
                update: updateFormatting(
                    ...extractAuditDataFromArray(data?.candidate?.ethnicities)
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data?.candidate?.ethnicities
                            ?.map(({ ethnicity }) => ethnicity?.id)
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateEthnicity({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    ethnicityId: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (response?.data?.updateEthnicity.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate race has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateEthnicity.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.ethnicities?.map(
                            (ethnicity) => ({
                                id: ethnicity.ethnicity?.id,
                                value: ethnicity.ethnicity?.value,
                                updatedAt: ethnicity.updatedAt,
                                updatedBy: ethnicity.updatedByUser,
                                createdAt: ethnicity.createdAt,
                                createdBy: ethnicity.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'Tier Ranking',
                id: 'tier',
                attribute: data.candidate.tier?.tier,
                options: data.tiers,
                update: updateFormatting(
                    data.candidate.tier?.updatedAt,
                    data.candidate.tier?.updatedByUser,
                    data.candidate.tier?.createdAt,
                    data.candidate.tier?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id === data.candidate?.tier?.tier?.id
                    if (hasNotChanged) return

                    try {
                        const response = await updateTier({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    tierId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response?.data?.updateTier.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate tier has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateTier?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.tier?.tier,
                    })
                },
            },
            {
                category: 'Hometown City',
                id: 'city',
                attribute: data.candidate.hometown?.city,
                freetext: true,
                update: updateFormatting(
                    data.candidate.hometown?.updatedAt,
                    data.candidate.hometown?.updatedByUser,
                    data.candidate.hometown?.createdAt,
                    data.candidate.hometown?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute === data.candidate?.hometown?.city
                    if (hasNotChanged) return

                    try {
                        const response = await updateHometown({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    city: row.attribute,
                                },
                            },
                        })
                        if (response?.data?.updateHometown.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate hometown has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateHometown?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: {
                            id: data.candidate?.hometown?.city,
                            value: data.candidate?.hometown?.city,
                        },
                    })
                },
            },
            {
                category: 'Hometown State/Province',
                id: 'state',
                attribute: stateOrProvinceAttribute,
                options: stateOrProvinceOptions,
                update: updateFormatting(
                    data.candidate.hometown?.updatedAt,
                    data.candidate.hometown?.updatedByUser,
                    data.candidate.hometown?.createdAt,
                    data.candidate.hometown?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    let isState =
                        data?.candidate?.hometown?.country?.value ===
                        'United States'
                    let isProvince =
                        data?.candidate?.hometown?.country?.value === 'Canada'
                    const hasNotChanged = isState
                        ? row.attribute?.id ===
                          data.candidate?.hometown?.state?.id
                        : isProvince
                          ? row.attribute?.id ===
                            data.candidate?.hometown?.province?.id
                          : false
                    if (hasNotChanged) return

                    // sending null instead of undefined when we are actually clearing out the entry - undefined would just be ignored
                    let newValue = row.attribute?.id || null
                    if (!!row.attribute?.id && row.options.length === 0) {
                        // edge case where we have a state/province preselected but the country is changed to something without state/province options and we need to clear the state/province selection
                        newValue = null
                        isState = true
                        isProvince = true
                    }
                    try {
                        const response = await updateHometown({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    // clear province when state is selected and vice versa
                                    stateId: isState ? newValue : null,
                                    provinceId: isProvince ? newValue : null,
                                },
                            },
                        })
                        if (response?.data?.updateHometown.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate hometown has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateHometown?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: {
                            id:
                                data.candidate?.hometown?.state?.id ||
                                data.candidate?.hometown?.province?.id,
                            value:
                                data.candidate?.hometown?.state?.value ||
                                data.candidate?.hometown?.province?.value,
                        },
                    })
                },
            },
            {
                category: 'Hometown Country',
                id: 'country',
                attribute: data.candidate.hometown?.country,
                options: data.countries,
                update: updateFormatting(
                    data.candidate.hometown?.updatedAt,
                    data.candidate.hometown?.updatedByUser,
                    data.candidate.hometown?.createdAt,
                    data.candidate.hometown?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id ===
                        data.candidate?.hometown?.country?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateHometown({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    countryId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response?.data?.updateHometown.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate hometown has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateHometown?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.hometown?.country,
                    })
                },
            },
            {
                category: 'Citizenships',
                id: 'citizenships',
                attribute:
                    data.candidate.citizenships?.map((citizenship) => ({
                        id: citizenship.citizenship?.id ?? '',
                        value: citizenship.citizenship?.value ?? '',
                        updatedAt: citizenship.updatedAt,
                        updatedBy: citizenship.updatedByUser,
                        createdAt: citizenship.createdAt,
                        createdBy: citizenship.createdByUser,
                    })) ?? [],
                options: data.citizenships,
                multiple: true,
                update: updateFormatting(
                    ...extractAuditDataFromArray(data?.candidate?.citizenships)
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data.candidate?.citizenships
                            ?.map(({ citizenship }) => citizenship?.id)
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateCitizenship({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    citizenshipIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (response?.data?.updateCitizenship.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate citizenships have been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateCitizenship?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.citizenships?.map(
                            (citizenship) => ({
                                id: citizenship.citizenship?.id,
                                value: citizenship.citizenship?.value,
                                updatedAt: citizenship.updatedAt,
                                updatedBy: citizenship.updatedByUser,
                                createdAt: citizenship.createdAt,
                                createdBy: citizenship.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'Visa',
                id: 'visa',
                attribute:
                    data.candidate.visaStatuses?.map((visaStatus) => ({
                        id: visaStatus.visaStatus?.id ?? '',
                        value: visaStatus.visaStatus?.value ?? '',
                        updatedAt: visaStatus.updatedAt,
                        updatedBy: visaStatus.updatedByUser,
                        createdAt: visaStatus.createdAt,
                        createdBy: visaStatus.createdByUser,
                    })) ?? [],
                options: data.visaStatuses,
                multiple: true,
                update: updateFormatting(
                    ...extractAuditDataFromArray(data?.candidate?.visaStatuses)
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data.candidate?.visaStatuses
                            ?.map(({ visaStatus }) => visaStatus?.id)
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateVisaStatus({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    visaStatusIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (response?.data?.updateVisaStatus.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate visa status has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateVisaStatus?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.visaStatuses?.map(
                            (visaStatus) => ({
                                id: visaStatus.visaStatus?.id,
                                value: visaStatus.visaStatus?.value,
                                updatedAt: visaStatus.updatedAt,
                                updatedBy: visaStatus.updatedByUser,
                                createdAt: visaStatus.createdAt,
                                createdBy: visaStatus.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'Languages',
                id: 'languages',
                attribute:
                    data.candidate.languages?.map((language) => ({
                        id: language.language?.id ?? '',
                        value: language.language?.value ?? '',
                        updatedAt: language.updatedAt,
                        updatedBy: language.updatedByUser,
                        createdAt: language.createdAt,
                        createdBy: language.createdByUser,
                    })) ?? [],
                options: data.languages,
                multiple: true,
                update: updateFormatting(
                    ...extractAuditDataFromArray(data?.candidate?.languages)
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data.candidate?.languages
                            ?.map(({ language }) => language?.id)
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateLanguages({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    languageIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (response?.data?.updateLanguages.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate languages have been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateLanguages?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.languages?.map((language) => ({
                            id: language.language?.id,
                            value: language.language?.value,
                            updatedAt: language.updatedAt,
                            updatedBy: language.updatedByUser,
                            createdAt: language.createdAt,
                            createdBy: language.createdByUser,
                        })),
                    })
                },
            },
            {
                category: 'Veteran',
                id: 'veteran',
                attribute: data.candidate.veteranStatus?.veteranStatus,
                options: data.veteranStatuses,
                update: updateFormatting(
                    data.candidate.veteranStatus?.updatedAt,
                    data.candidate.veteranStatus?.updatedByUser,
                    data.candidate.veteranStatus?.createdAt,
                    data.candidate.veteranStatus?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id ===
                        data.candidate?.veteranStatus?.veteranStatus?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateVeteranStatus({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    veteranStatusId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response?.data?.updateVeteranStatus.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate veteran status has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateVeteranStatus?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.veteranStatus?.veteranStatus,
                    })
                },
            },
            {
                category: 'Qualitative',
                id: 'qualitative',
                attribute:
                    data.candidate.personalityAttributes?.map(
                        (personalityAttribute) => ({
                            id:
                                personalityAttribute.personalityAttribute?.id ??
                                '',
                            value:
                                personalityAttribute.personalityAttribute
                                    ?.value ?? '',
                            updatedAt: personalityAttribute.updatedAt,
                            updatedBy: personalityAttribute.updatedByUser,
                            createdAt: personalityAttribute.createdAt,
                            createdBy: personalityAttribute.createdByUser,
                        })
                    ) ?? [],
                multiple: true,
                options: data.personalityAttributes,
                update: updateFormatting(
                    ...extractAuditDataFromArray(
                        data?.candidate?.personalityAttributes
                    )
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data?.candidate?.personalityAttributes
                            ?.map(
                                ({ personalityAttribute }) =>
                                    personalityAttribute?.id
                            )
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updatePersonalityAttributes({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    personalityAttributeIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (
                            response.data?.updateCandidatePersonalityAttributes
                                .success
                        ) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate qualitative has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data
                                    ?.updateCandidatePersonalityAttributes
                                    ?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.ethnicities?.map(
                            (ethnicity) => ({
                                id: ethnicity.ethnicity?.id,
                                value: ethnicity.ethnicity?.value,
                                updatedAt: ethnicity.updatedAt,
                                updatedBy: ethnicity.updatedByUser,
                                createdAt: ethnicity.createdAt,
                                createdBy: ethnicity.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'Revenue Experience',
                id: 'revenueExperience',
                attribute:
                    data.candidate.revenueExperiences?.map(
                        (revenueExperience) => ({
                            id: revenueExperience.revenueExperience?.id ?? '',
                            value:
                                revenueExperience.revenueExperience?.value ??
                                '',
                            updatedAt: revenueExperience.updatedAt,
                            updatedBy: revenueExperience.updatedByUser,
                            createdAt: revenueExperience.createdAt,
                            createdBy: revenueExperience.createdByUser,
                        })
                    ) ?? [],
                options: data.revenueExperiences,
                multiple: true,
                update: updateFormatting(
                    ...extractAuditDataFromArray(
                        data.candidate.revenueExperiences
                    )
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data.candidate?.revenueExperiences
                            ?.map(
                                ({ revenueExperience }) => revenueExperience?.id
                            )
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateRevenueExperience({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    revenueExperienceIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (
                            response?.data?.updateCandidateRevenueExperience
                                .success
                        ) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate revenue experience has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateCandidateRevenueExperience
                                    ?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.revenueExperiences?.map(
                            (revenueExperience) => ({
                                id: revenueExperience.revenueExperience?.id,
                                value: revenueExperience.revenueExperience
                                    ?.value,
                                updatedAt: revenueExperience.updatedAt,
                                updatedBy: revenueExperience.updatedByUser,
                                createdAt: revenueExperience.createdAt,
                                createdBy: revenueExperience.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'Real Estate Deal Experience',
                id: 'realEstateDealExperience',
                attribute:
                    data.candidate.realEstateDealExperiences?.map(
                        (reDealExp) => ({
                            id: reDealExp.realEstateDealExperience?.id ?? '',
                            value:
                                reDealExp.realEstateDealExperience?.value ?? '',
                            updatedAt: reDealExp.updatedAt,
                            updatedBy: reDealExp.updatedByUser,
                            createdAt: reDealExp.createdAt,
                            createdBy: reDealExp.createdByUser,
                        })
                    ) ?? [],
                multiple: true,
                options: data.realEstateDealExperiences,
                update: updateFormatting(
                    ...extractAuditDataFromArray(
                        data?.candidate?.realEstateDealExperiences
                    )
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data?.candidate?.realEstateDealExperiences
                            ?.map(
                                ({ realEstateDealExperience }) =>
                                    realEstateDealExperience?.id
                            )
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateRealEstateDealExperience({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    realEstateDealExperienceIds:
                                        row.attribute.map(
                                            (option) => option.id
                                        ),
                                },
                            },
                        })
                        if (
                            response.data
                                ?.updateCandidateRealEstateDealExperience
                                .success
                        ) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate Real Estate Deal Experience has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data
                                    ?.updateCandidateRealEstateDealExperience
                                    ?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.realEstateDealExperiences?.map(
                            (reDealExp) => ({
                                id: reDealExp.realEstateDealExperience?.id,
                                value: reDealExp.realEstateDealExperience
                                    ?.value,
                                updatedAt: reDealExp.updatedAt,
                                updatedBy: reDealExp.updatedByUser,
                                createdAt: reDealExp.createdAt,
                                createdBy: reDealExp.createdByUser,
                            })
                        ),
                    })
                },
            },
            {
                category: 'LGBTQ',
                id: 'LGBTQ',
                attribute: data.candidate.lgbtq?.lgbtq,
                options: data.lgbtq,
                update: updateFormatting(
                    data.candidate.lgbtq?.updatedAt,
                    data.candidate.lgbtq?.updatedByUser,
                    data.candidate.lgbtq?.createdAt,
                    data.candidate.lgbtq?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id === data.candidate?.lgbtq?.lgbtq?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateLgbtq({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    lgbtqId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response.data?.updateLgbtq.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate LGBTQ has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateLgbtq?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.lgbtq?.lgbtq,
                    })
                },
            },
            {
                category: 'Deal Sourcing Role',
                id: 'dealSourcing',
                attribute: data.candidate.dealSourcingRole?.dealSourcingRole,
                options: data.dealSourcingRoles,
                update: updateFormatting(
                    data.candidate.dealSourcingRole?.updatedAt,
                    data.candidate.dealSourcingRole?.updatedByUser,
                    data.candidate.dealSourcingRole?.createdAt,
                    data.candidate.dealSourcingRole?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id ===
                        data.candidate?.dealSourcingRole?.dealSourcingRole?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateDealSourcingRole({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    dealSourcingRoleId:
                                        row.attribute?.id || null,
                                },
                            },
                        })
                        if (response.data?.updateDealSourcingRole.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate Deal Sourcing Role has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateDealSourcingRole?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.dealSourcingRole
                            ?.dealSourcingRole,
                    })
                },
            },
            {
                category: 'CPI Placed',
                id: 'cpiPlaced',
                attribute:
                    data.candidate.cpiPlaced?.map((cpiPlaced) => ({
                        id: cpiPlaced.cpiPlaced?.id ?? '',
                        value: cpiPlaced.cpiPlaced?.value ?? '',
                        updatedAt: cpiPlaced.updatedAt,
                        updatedBy: cpiPlaced.updatedByUser,
                        createdAt: cpiPlaced.createdAt,
                        createdBy: cpiPlaced.createdByUser,
                    })) ?? [],
                options: data.cpiPlaced,
                multiple: true,
                update: updateFormatting(
                    ...extractAuditDataFromArray(data?.candidate?.cpiPlaced)
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )

                    const hasNotChanged = isDeepEqual(
                        row.attribute
                            .map((option) => option.id)
                            .filter(Boolean),
                        data.candidate?.cpiPlaced
                            ?.map(({ cpiPlaced }) => cpiPlaced?.id)
                            .filter(Boolean)
                    )

                    if (hasNotChanged) return

                    try {
                        const response = await updateCpiPlaced({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    cpiPlacedIds: row.attribute.map(
                                        (option) => option.id
                                    ),
                                },
                            },
                        })
                        if (response?.data?.updateCpiPlaced.success) {
                            refetchAttributes()
                            refetch() // also refetching profile data because this attribute affects outreach status
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate CPI Placed has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateCpiPlaced?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.cpiPlaced?.map((cpiPlaced) => ({
                            id: cpiPlaced.cpiPlaced?.id,
                            value: cpiPlaced.cpiPlaced?.value,
                            updatedAt: cpiPlaced.updatedAt,
                            updatedBy: cpiPlaced.updatedByUser,
                            createdAt: cpiPlaced.createdAt,
                            createdBy: cpiPlaced.createdByUser,
                        })),
                    })
                },
            },
            {
                category: 'Reference',
                id: 'reference',
                attribute:
                    data.candidate.referenceAttribute?.referenceAttribute,
                options: data.referenceAttributes,
                update: updateFormatting(
                    data.candidate.referenceAttribute?.updatedAt,
                    data.candidate.referenceAttribute?.updatedByUser,
                    data.candidate.referenceAttribute?.createdAt,
                    data.candidate.referenceAttribute?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id ===
                        data.candidate?.referenceAttribute?.referenceAttribute
                            ?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateReferenceAttribute({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    referenceAttributeId:
                                        row.attribute?.id || null,
                                },
                            },
                        })
                        if (response?.data?.updateReferenceAttribute.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message:
                                    'Candidate Reference Attribute has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateReferenceAttribute?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.referenceAttribute
                            ?.referenceAttribute,
                    })
                },
            },
            {
                category: 'MBA Tier',
                id: 'mbaTier',
                attribute: data.candidate.mbaTier?.mbaTier,
                options: data.mbaTiers,
                update: updateFormatting(
                    data.candidate.mbaTier?.updatedAt,
                    data.candidate.mbaTier?.updatedByUser,
                    data.candidate.mbaTier?.createdAt,
                    data.candidate.mbaTier?.createdByUser
                ),
                handleSave: async (params: GridCellEditStopParams) => {
                    const row = gridRef.current.getRowWithUpdatedValues(
                        params.id,
                        params.field
                    )
                    const hasNotChanged =
                        row.attribute?.id ===
                        data.candidate?.mbaTier?.mbaTier?.id
                    if (hasNotChanged) return
                    try {
                        const response = await updateCandidateMbaTier({
                            variables: {
                                input: {
                                    candidateId: candidateId || '',
                                    mbaTiersId: row.attribute?.id || null,
                                },
                            },
                        })
                        if (response.data?.updateCandidateMbaTier.success) {
                            refetchAttributes()
                            toast({
                                severity: 'success',
                                title: 'Success',
                                message: 'Candidate MBA Tier has been updated',
                            })
                        } else {
                            handleResetWithErrorToast(
                                params,
                                response.data?.updateCandidateMbaTier?.message
                            )
                        }
                    } catch (e) {
                        handleResetWithErrorToast(params)
                    }
                },
                handleReset: async (params: GridCellEditStopParams) => {
                    gridRef.current?.setEditCellValue({
                        id: params.id,
                        field: params.field,
                        value: data.candidate?.mbaTier?.mbaTier,
                    })
                },
            },
        ]
    }, [
        candidateId,
        data,
        gridRef,
        handleResetWithErrorToast,
        refetch,
        refetchAttributes,
        stateOrProvinceAttribute,
        stateOrProvinceOptions,
        toast,
        updateAnalystClassYear,
        updateCandidateMbaTier,
        updateCitizenship,
        updateCpiPlaced,
        updateDealSourcingRole,
        updateEthnicity,
        updateGender,
        updateHometown,
        updateLanguages,
        updateLgbtq,
        updatePersonalityAttributes,
        updateRealEstateDealExperience,
        updateReferenceAttribute,
        updateRevenueExperience,
        updateTier,
        updateVeteranStatus,
        updateVisaStatus,
    ])

    return (
        <Grid item xs={7}>
            <Card>
                <Typography variant="h4">Attributes</Typography>
                <DataGridPro
                    apiRef={gridRef}
                    rowSelection={false}
                    disableColumnSelector
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSorting
                    hideFooter
                    autoHeight
                    getRowHeight={() => 'auto'}
                    sx={{ border: 0 }}
                    onCellEditStop={(params: GridCellEditStopParams) => {
                        // each row has a handleSave and handleReset function defined separately since they have different API calls to update / data points to reset to
                        if (params.reason !== 'escapeKeyDown') {
                            params.row.handleSave(params)
                        } else {
                            params.row.handleReset(params)
                        }
                    }}
                    columns={[
                        {
                            field: 'category',
                            headerName: 'Category',
                            editable: false,
                            flex: 1,
                            renderCell: (params) => {
                                return (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '100%',
                                            minHeight: getPxAsRem(52),
                                        }}
                                    >
                                        {params.value}
                                    </Typography>
                                )
                            },
                        },
                        {
                            field: 'attribute',
                            headerName: 'Attribute',
                            editable: true,
                            renderCell: (params) => <ChipCell {...params} />,
                            renderEditCell: (params) => (
                                <EditCell {...params} />
                            ),
                            flex: 2,
                        },
                        {
                            field: 'update',
                            headerName: 'Update',
                            editable: false,
                            flex: 2,
                            renderCell: (params) => {
                                return (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            height: '100%',
                                            minHeight: getPxAsRem(52),
                                        }}
                                    >
                                        {params.value}
                                    </Typography>
                                )
                            },
                        },
                    ]}
                    rows={rows}
                />
            </Card>
        </Grid>
    )
}

export default Attributes
